// RefereeScoringPad — extracted from TournamentDetail (Iter 30 refactor).
// Renders one of three artwork variants (polish_bladers / beyblade_polska /
// pokeblade), overlaying invisible % hit zones over baked-in button labels.
// Falls back to SimpleRefereePad when the tournament uses a format without
// artwork. Also exports the small helpers used by both pads.
import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "@phosphor-icons/react";
import { t } from "../../lib/i18n";

export default function RefereeScoringPad({ selected, playerA, playerB, limit, finishEntries, busy, recordFinish, setActiveBey, beyOptionsA, beyOptionsB, isPolska, isPokeblade, isStandard, undo, forceComplete, goNext }) {
  const [swapped, setSwapped] = useState(false);
  const [timerSec, setTimerSec] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const toastTimer = useRef(null);
  // Iter 43 — dismissable gesture-hint bar. Persisted in localStorage so the
  // referee only sees the tip until they close it once on this device.
  const [showHint, setShowHint] = useState(() => {
    try { return localStorage.getItem("ref-gesture-hint-dismissed") !== "1"; }
    catch { return true; }
  });
  const dismissHint = () => {
    setShowHint(false);
    try { localStorage.setItem("ref-gesture-hint-dismissed", "1"); } catch (_) {}
  };
  // Long-press progress ring state (0..1) while the referee holds the center
  // of the pad. Rendered as a radial gauge that fills over 1 second.
  const [holdProgress, setHoldProgress] = useState(0);

  useEffect(() => {
    setTimerSec(0);
    setTimerRunning(false);
    setSwapped(false);
  }, [selected?.id]);

  useEffect(() => {
    if (!timerRunning) return undefined;
    const id = setInterval(() => setTimerSec((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [timerRunning]);

  // Iter 43 — Referee quick gestures (Standard/Polska/Pokeblade only; the
  // BEY-PONG referee pad has its own state machine and is a different comp).
  //
  //   • Swipe LEFT (>60px, mostly-horizontal) → undo last finish.
  //   • Long-press (1s hold, movement <10px) on the pad center → force-end.
  //
  // Implementation notes:
  //   - Native pointer listeners attached to `padRef` so we can call
  //     `preventDefault()` on the follow-up `click` and stop it from
  //     activating the HitZone underneath. React synthetic handlers can't
  //     see the click at capture phase reliably across touch devices.
  //   - We only fire the long-press if the pointer stays inside a
  //     center "safe zone" (avoids the scoring buttons top/bottom).
  const padRef = useRef(null);
  const gestureRef = useRef({
    x: 0, y: 0, t: 0, active: false, fired: false,
    longPressTimer: null, progressTimer: null,
  });
  const showToastRef = useRef(null);
  useEffect(() => {
    const el = padRef.current;
    if (!el) return undefined;
    // Skip BEY-PONG (variant is set later; recompute here)
    const CENTER_TOP = 0.35;   // 35%..75% of pad height is the long-press safe zone
    const CENTER_BOT = 0.75;
    const SWIPE_PX = 60;
    const TAP_SLOP = 10;
    const HOLD_MS = 1000;

    const cancelHold = () => {
      const g = gestureRef.current;
      if (g.longPressTimer) { clearTimeout(g.longPressTimer); g.longPressTimer = null; }
      if (g.progressTimer) { clearInterval(g.progressTimer); g.progressTimer = null; }
      setHoldProgress(0);
    };

    const onPointerDown = (e) => {
      // Ignore multi-touch (pinch/zoom) and non-primary buttons on mouse.
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const rect = el.getBoundingClientRect();
      const relY = (e.clientY - rect.top) / rect.height;
      gestureRef.current = {
        x: e.clientX, y: e.clientY, t: Date.now(),
        active: true, fired: false,
        longPressTimer: null, progressTimer: null,
        startRelY: relY,
      };
      // Only arm long-press if pointerdown started in the center vertical band.
      if (relY >= CENTER_TOP && relY <= CENTER_BOT) {
        const start = Date.now();
        gestureRef.current.progressTimer = setInterval(() => {
          const p = Math.min(1, (Date.now() - start) / HOLD_MS);
          setHoldProgress(p);
        }, 40);
        gestureRef.current.longPressTimer = setTimeout(async () => {
          const g = gestureRef.current;
          if (!g.active) return;
          g.fired = true;
          cancelHold();
          if (navigator.vibrate) { try { navigator.vibrate([15, 40, 15]); } catch (_) {} }
          if (typeof forceComplete === "function") {
            const res = await forceComplete();
            if (res && res.ok && showToastRef.current) {
              showToastRef.current(t.gestureEndToast || "MATCH ENDED");
            }
          }
        }, HOLD_MS);
      }
    };
    const onPointerMove = (e) => {
      const g = gestureRef.current;
      if (!g.active) return;
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) cancelHold();
      // Left swipe: horizontal-dominant, moved >SWIPE_PX to the left.
      if (!g.fired && dx <= -SWIPE_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
        g.fired = true;
        cancelHold();
        if (navigator.vibrate) { try { navigator.vibrate(20); } catch (_) {} }
        if (typeof undo === "function") undo();
        if (showToastRef.current) showToastRef.current(t.gestureUndoToast || "UNDONE");
      }
    };
    const onPointerUp = (e) => {
      cancelHold();
      const g = gestureRef.current;
      g.active = false;
      if (g.fired) {
        // Suppress the synthetic click that would otherwise fire the HitZone
        // under the release point.
        const suppress = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener("click", suppress, true);
        };
        document.addEventListener("click", suppress, true);
        // Safety: remove after one tick even if no click ever fires.
        setTimeout(() => document.removeEventListener("click", suppress, true), 400);
      }
    };
    const onPointerCancel = () => {
      cancelHold();
      gestureRef.current.active = false;
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerCancel);
    el.addEventListener("pointerleave", onPointerCancel);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerCancel);
      el.removeEventListener("pointerleave", onPointerCancel);
      cancelHold();
    };
  }, [undo, forceComplete]);

  const showToast = (msg) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(""), 900);
  };
  // Expose the latest showToast to the pointer-listener effect (which is
  // memoized and cannot re-close over new function references).
  showToastRef.current = showToast;

  const mm = String(Math.floor(timerSec / 60)).padStart(2, "0");
  const ss = String(timerSec % 60).padStart(2, "0");
  const done = selected.status === "completed";

  // Left/right visual positions follow the swap toggle so scores stay tied
  // to their player.
  const left = swapped
    ? { player: playerB, score: selected.score_b, id: selected.player_b_id }
    : { player: playerA, score: selected.score_a, id: selected.player_a_id };
  const right = swapped
    ? { player: playerA, score: selected.score_a, id: selected.player_a_id }
    : { player: playerB, score: selected.score_b, id: selected.player_b_id };

  // Positional mapping f1..f6 → finishEntries. Standard artwork orders its
  // buttons Extreme→Spin→Over→Out→Burst; we reorder to keep hit zones aligned
  // with the visible labels while backend point values remain authoritative.
  const STANDARD_ORDER = ["xtreme", "spin", "over", "out", "burst"];
  const orderedFinishes = (isPolska || !isStandard)
    ? finishEntries
    : STANDARD_ORDER
        .map((k) => finishEntries.find(([key]) => key === k))
        .filter(Boolean);
  const finishBySlot = orderedFinishes.slice(0, 6);
  const clickFinish = (slotIdx, isLeft) => {
    const entry = finishBySlot[slotIdx];
    if (!entry) return;
    const [key] = entry;
    const targetId = isLeft ? left.id : right.id;
    if (!targetId) return;
    recordFinish(targetId, key);
  };

  const canUndo = !busy && (selected.finishes || []).length > 0;

  // Iter 27 — three artwork variants:
  //   - standard  (polish_bladers)  → /referee-panel-standard.png + 5 finish buttons per side
  //   - polska    (beyblade_polska) → /referee-panel-bg.jpg      + 6 finish buttons per side
  //   - pokeblade (pokeblade)       → /referee-panel-pokeblade.png + single ZWYCIĘZCA per side
  // Any other future format falls back to the plain text pad.
  let variant = null;
  let bgSrc = null;
  let panelAspect = "877 / 1536";
  if (isPolska) {
    variant = "polska";
    bgSrc = "/referee-panel-bg.jpg";
  } else if (isPokeblade) {
    variant = "pokeblade";
    bgSrc = "/referee-panel-pokeblade.png";
    panelAspect = "948 / 1660";
  } else if (isStandard) {
    variant = "standard";
    bgSrc = "/referee-panel-standard.png";
    panelAspect = "948 / 1660";
  }

  if (!variant) {
    return (
      <SimpleRefereePad
        selected={selected}
        playerA={playerA}
        playerB={playerB}
        limit={limit}
        finishEntries={finishEntries}
        busy={busy}
        recordFinish={recordFinish}
        setActiveBey={setActiveBey}
        beyOptionsA={beyOptionsA}
        beyOptionsB={beyOptionsB}
        undo={undo}
        goNext={goNext}
      />
    );
  }

  // In Pokeblade the visible score is REMAINING lives (6 → 0), which is the
  // mirror of the opponent's deck_win count.
  const displayScore = (side) => {
    if (variant !== "pokeblade") return side.score;
    const opponentScore = side === left ? selected.score_b : selected.score_a;
    return Math.max(0, (limit || 6) - (opponentScore || 0));
  };

  // Pokeblade ZWYCIĘZCA button awards a deck_win to the SCORING player,
  // which decrements the opponent's displayed lives by 1.
  const clickWinner = (isLeft) => {
    const winnerId = isLeft ? left.id : right.id;
    if (!winnerId) return;
    recordFinish(winnerId, "deck_win");
  };

  return (
    <div className="mx-auto w-full max-w-[560px]">
      {/* Iter 43 — dismissable gesture hint. Appears once per device until
          the referee taps "Got it". Kept compact so it doesn't push the
          artwork off-screen on shorter phones. */}
      {showHint && (
        <div
          data-testid="ref-gesture-hint"
          className="mb-2 flex items-center gap-2 border border-[#E2FF00]/30 bg-[#E2FF00]/[0.04] px-2 py-1.5"
        >
          <span
            aria-hidden="true"
            className="text-[#E2FF00] font-mono text-[10px] font-bold tracking-[0.15em]"
          >
            REF
          </span>
          <div className="flex-1 min-w-0 text-[10px] font-mono uppercase tracking-[0.12em] text-white/70 leading-tight">
            <div className="truncate">{t.gestureHintUndo || "← Swipe left to undo"}</div>
            <div className="truncate">{t.gestureHintEnd || "Hold center 1s to end match"}</div>
          </div>
          <button
            type="button"
            onClick={dismissHint}
            data-testid="ref-gesture-hint-dismiss"
            className="shrink-0 px-2 py-1 border border-white/25 text-[10px] uppercase tracking-[0.2em] font-bold text-white/70 hover:text-white hover:border-white/50"
          >
            {t.gestureHintDismiss || "Got it"}
          </button>
        </div>
      )}
      {/* Iter 26 — Active Bey pickers per player. Each finish scored while
          a Bey is set here will be attributed to that Bey in stats. */}
      <div
        data-testid="ref-active-bey-row"
        className="grid grid-cols-2 gap-2 mb-3 text-xs"
      >
        <BeyPicker
          side="a"
          playerId={selected.player_a_id}
          playerName={playerA?.handle}
          value={selected.active_bey_a_id || ""}
          onChange={(v) => setActiveBey && setActiveBey(selected.player_a_id, v)}
          options={beyOptionsA || []}
          accent="#00E7F4"
        />
        <BeyPicker
          side="b"
          playerId={selected.player_b_id}
          playerName={playerB?.handle}
          value={selected.active_bey_b_id || ""}
          onChange={(v) => setActiveBey && setActiveBey(selected.player_b_id, v)}
          options={beyOptionsB || []}
          accent="#FF4F8C"
        />
      </div>

      <div
        ref={padRef}
        className="relative w-full touch-pan-y select-none"
        style={{
          aspectRatio: panelAspect,
          // Iter 41 — container queries. Every overlay inside this wrapper
          // now sizes off the wrapper's width (`cqw`) instead of the
          // viewport, so timer/reset/score/stage all shrink in lockstep
          // with the artwork on mobile widths.
          containerType: "inline-size",
        }}
      >
      {/* Artwork background */}
      <img
        src={bgSrc}
        alt=""
        draggable={false}
        data-testid={`ref-panel-bg-${variant}`}
        className="absolute inset-0 w-full h-full pointer-events-none select-none"
      />

      {/* Player name plates */}
      <PlateInput
        value={left.player?.handle || "GRACZ 1"}
        color="#00E7F4"
        style={{ left: "20.98%", top: "7.23%", width: "21.66%", height: "2.21%" }}
        testid="ref-name-left"
      />
      <PlateInput
        value={right.player?.handle || "GRACZ 2"}
        color="#FF4F8C"
        style={{ left: "62.83%", top: "7.23%", width: "21.66%", height: "2.21%" }}
        testid="ref-name-right"
      />

      {/* Score displays */}
      <ScoreDisplay
        score={displayScore(left)}
        color="#00E7F4"
        style={{ left: "23.72%", top: "11.72%", width: "9.35%", height: "3.13%" }}
        testid="ref-score-left"
      />
      <ScoreDisplay
        score={displayScore(right)}
        color="#FF4F8C"
        style={{ left: "66.70%", top: "11.72%", width: "9.35%", height: "3.13%" }}
        testid="ref-score-right"
      />

      {/* Iter 34 — stage + effective score limit caption. Overlays the
          artwork in a neutral top-center band so referees can see at a
          glance which threshold applies (7 in groups vs 10 in knockout). */}
      <div
        data-testid="ref-stage-limit"
        className="absolute z-20 left-1/2 -translate-x-1/2 rounded border border-white/25 bg-black/70 text-white/85 font-mono uppercase tracking-[0.2em] flex items-center"
        style={{
          top: "5.66%",
          // Iter 41 — cqw units so this badge scales with the artwork.
          fontSize: "clamp(8px, 1.9cqw, 12px)",
          gap: "clamp(2px, 0.7cqw, 6px)",
          padding: "clamp(1px, 0.35cqw, 3px) clamp(4px, 1.2cqw, 10px)",
        }}
      >
        <span className="text-white/50">{selected.stage === "knockout" ? "KO" : "GROUP"}</span>
        <span className="text-white/30">·</span>
        <span className="text-[#E2FF00] tabular-nums">/{limit}</span>
      </div>

      {/* Timer overlay (top-left unused area of artwork) */}
      <button
        onClick={() => setTimerRunning((r) => !r)}
        data-testid="ref-timer-toggle"
        title={t.roundTimer || "Round timer"}
        className="absolute z-20 flex items-center rounded border border-white/30 bg-black/70 text-[#E2FF00] font-mono font-bold"
        style={{
          left: "3%",
          top: "1.5%",
          fontSize: "clamp(9px, 2.1cqw, 14px)",
          gap: "clamp(3px, 0.9cqw, 7px)",
          padding: "clamp(2px, 0.7cqw, 6px) clamp(4px, 1.4cqw, 10px)",
        }}
      >
        {/* Iter 42 — icon inherits `em` so it scales with button fontSize. */}
        {timerRunning ? <Pause size="1em" weight="fill" /> : <Play size="1em" weight="fill" />}
        <span data-testid="ref-timer-display" className="tabular-nums">{mm}:{ss}</span>
      </button>

      {/* Reset button hit zone */}
      <HitZone
        style={{ left: "34.21%", top: "1.43%", width: "31.93%", height: "4.56%" }}
        onClick={() => { undo(); showToast(t.resetLastToast || "RESET"); }}
        disabled={!canUndo}
        testid="ref-reset-last"
        ariaLabel={t.resetLast}
      />

      {/* Finish hit zones — layout differs per variant:
          - polska:   6 rows of finish buttons per side (spin/over/burst/xtreme/tybuch/zetek)
          - standard: 5 rows of finish buttons per side (extreme/spin/over/out/burst)
          - pokeblade: single centered ZWYCIĘZCA button per side (awards deck_win). */}
      {variant !== "pokeblade" && finishBySlot.map((entry, i) => (
        <span key={entry[0]}>
          <HitZone
            style={{
              left: "4.56%",
              top: `${58.79 + i * 5.93}%`,
              width: "44.01%",
              height: "5.21%",
            }}
            onClick={() => clickFinish(i, true)}
            disabled={busy || done}
            testid={`ref-finish-a-${entry[0]}`}
            ariaLabel={`${left.player?.handle || "Gracz 1"} — ${entry[1].label} (+${entry[1].points})`}
          />
          <HitZone
            style={{
              left: "51.43%",
              top: `${58.79 + i * 5.93}%`,
              width: "44.01%",
              height: "5.21%",
            }}
            onClick={() => clickFinish(i, false)}
            disabled={busy || done}
            testid={`ref-finish-b-${entry[0]}`}
            ariaLabel={`${right.player?.handle || "Gracz 2"} — ${entry[1].label} (+${entry[1].points})`}
          />
        </span>
      ))}

      {variant === "pokeblade" && (
        <>
          <HitZone
            style={{ left: "5.30%", top: "63.55%", width: "40.72%", height: "6.30%" }}
            onClick={() => clickWinner(true)}
            disabled={busy || done}
            testid="ref-poke-winner-a"
            ariaLabel={`${left.player?.handle || "Gracz 1"} — ${t.pokebladeBeyLost}`}
          />
          <HitZone
            style={{ left: "53.98%", top: "63.55%", width: "40.72%", height: "6.30%" }}
            onClick={() => clickWinner(false)}
            disabled={busy || done}
            testid="ref-poke-winner-b"
            ariaLabel={`${right.player?.handle || "Gracz 2"} — ${t.pokebladeBeyLost}`}
          />
        </>
      )}

      {/* Bottom nav: back, swap, next */}
      <HitZone
        style={{ left: "6.73%", top: "91.80%", width: "20.18%", height: "4.88%" }}
        onClick={undo}
        disabled={!canUndo}
        testid="ref-back"
        ariaLabel={t.back}
      />
      <HitZone
        style={{ left: "31.70%", top: "91.47%", width: "35.35%", height: "5.34%" }}
        onClick={() => { setSwapped((s) => !s); showToast(t.swapSidesToast || "ZMIANA STRON"); }}
        testid="ref-swap-sides"
        ariaLabel={t.swapSides}
      />
      <HitZone
        style={{ left: "72.75%", top: "91.80%", width: "20.18%", height: "4.88%" }}
        onClick={goNext}
        testid="ref-next"
        ariaLabel={t.next}
      />

      {holdProgress > 0.05 && (
        <div
          data-testid="ref-hold-progress"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none flex items-center justify-center"
          style={{
            width: "clamp(72px, 22cqw, 140px)",
            height: "clamp(72px, 22cqw, 140px)",
          }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="44" fill="rgba(3,8,12,0.72)" stroke="rgba(255,255,255,0.18)" strokeWidth="4" />
            <circle
              cx="50" cy="50" r="44"
              fill="none"
              stroke="#FF0055"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 44}
              strokeDashoffset={2 * Math.PI * 44 * (1 - holdProgress)}
              style={{ transition: "stroke-dashoffset 40ms linear" }}
            />
          </svg>
          <div
            className="absolute inset-0 flex items-center justify-center text-center px-1 font-mono font-bold text-[#FF0055] uppercase tracking-[0.12em]"
            style={{ fontSize: "clamp(8px, 2.2cqw, 13px)", lineHeight: 1.1 }}
          >
            {t.gestureEndHold || "HOLD TO END…"}
          </div>
        </div>
      )}

      {toastMsg && (
        <div
          data-testid="ref-toast"
          className="absolute left-1/2 -translate-x-1/2 z-30 px-3 py-1.5 border border-white/20 rounded bg-black/80 text-white text-xs font-mono uppercase tracking-[0.15em]"
          style={{ top: "5.66%" }}
        >
          {toastMsg}
        </div>
      )}
      </div>
    </div>
  );
}

function SimpleRefereePad({ selected, playerA, playerB, limit, finishEntries, busy, recordFinish, setActiveBey, beyOptionsA, beyOptionsB, undo, goNext }) {
  const done = selected.status === "completed";
  const canUndo = !busy && (selected.finishes || []).length > 0;
  return (
    <div className="border border-white/10 bg-[#0C0C14] p-5 space-y-5" data-testid="ref-simple-pad">
      <div className="grid grid-cols-2 gap-4">
        <PlayerCol
          label="A"
          player={playerA}
          score={selected.score_a}
          limit={limit}
          winner={selected.winner_id === selected.player_a_id}
          activeBey={selected.active_bey_a_id}
          onBey={(v) => setActiveBey && setActiveBey(selected.player_a_id, v)}
          entries={finishEntries}
          disabled={busy || done}
          onFinish={(k) => recordFinish(selected.player_a_id, k)}
          beyOptions={beyOptionsA}
          accent="#00E5FF"
          testidSide="a"
        />
        <PlayerCol
          label="B"
          player={playerB}
          score={selected.score_b}
          limit={limit}
          winner={selected.winner_id === selected.player_b_id}
          activeBey={selected.active_bey_b_id}
          onBey={(v) => setActiveBey && setActiveBey(selected.player_b_id, v)}
          entries={finishEntries}
          disabled={busy || done}
          onFinish={(k) => recordFinish(selected.player_b_id, k)}
          beyOptions={beyOptionsB}
          accent="#FF0055"
          testidSide="b"
        />
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={undo}
          disabled={!canUndo}
          data-testid="ref-back"
          className="px-4 py-2 border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-xs uppercase tracking-[0.25em] font-bold disabled:opacity-30"
        >
          ↶ {t.undoLast}
        </button>
        <button
          onClick={goNext}
          data-testid="ref-next"
          className="px-4 py-2 border border-[#FF0055]/40 text-[#FF0055] hover:bg-[#FF0055]/10 text-xs uppercase tracking-[0.25em] font-bold"
        >
          {t.next} »
        </button>
        <span className="text-[10px] font-mono text-white/40">
          {(selected.finishes || []).length} finishes
        </span>
      </div>
    </div>
  );
}

function PlayerCol({ label, player, score, limit, winner, activeBey, onBey, entries, disabled, onFinish, beyOptions, accent, testidSide }) {
  return (
    <div
      data-testid={`ref-player-${testidSide}`}
      className="border p-4 space-y-3"
      style={{ borderColor: winner ? accent : "rgba(255,255,255,0.15)", background: winner ? `${accent}08` : "transparent" }}
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: accent }}>
        {label}-Side
      </div>
      <div className="font-display font-black italic uppercase text-lg truncate">{player?.handle || "TBD"}</div>
      <div className="font-display font-black italic tabular-nums text-5xl">
        {score}<span className="text-white/30 text-xl">/{limit}</span>
      </div>
      <select
        value={activeBey || ""}
        onChange={(e) => onBey(e.target.value)}
        disabled={!player}
        data-testid={`ref-bey-select-${testidSide}`}
        className="w-full bg-transparent border border-white/15 px-2 py-1.5 text-xs focus:outline-none focus:border-white/30"
      >
        <option value="" className="bg-[#0C0C14]">— Active Bey —</option>
        {(beyOptions || []).map((o) => (
          <option key={o.id} value={o.id} className="bg-[#0C0C14]">{o.name}</option>
        ))}
      </select>
      <div className="space-y-1.5">
        {entries.map(([key, def]) => (
          <button
            key={key}
            onClick={() => onFinish(key)}
            disabled={disabled}
            data-testid={`ref-finish-${testidSide}-${key}`}
            className="w-full text-left px-3 py-2 border text-xs uppercase tracking-[0.15em] font-bold disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            style={{ borderColor: `${accent}55`, color: accent }}
            onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = `${accent}11`; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
          >
            {def.label} <span className="text-white/60 font-mono">+{def.points}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function BeyPicker({ playerId, playerName, value, onChange, options, accent, side }) {
  return (
    <div
      data-testid={`ref-bey-picker-${side}`}
      className="border rounded-md px-2 py-1.5"
      style={{ borderColor: `${accent}55`, background: `${accent}0d` }}
    >
      <div
        className="text-[9px] uppercase tracking-[0.2em] font-mono font-bold truncate"
        style={{ color: accent }}
      >
        {playerName || `Gracz ${side === "a" ? 1 : 2}`}
      </div>
      <select
        value={value || ""}
        disabled={!playerId}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`ref-bey-select-${side}`}
        className="mt-1 w-full bg-transparent border border-white/10 rounded px-1.5 py-1 text-[11px] focus:outline-none focus:border-white/30"
      >
        <option value="" className="bg-[#0C0C14]">— Active Bey —</option>
        {options.map((o) => (
          <option key={o.id} value={o.id} className="bg-[#0C0C14]">
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function HitZone({ style, onClick, disabled, testid, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      aria-label={ariaLabel}
      className="absolute z-10 border-0 p-0 m-0 bg-transparent cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/50 hover:bg-white/[0.04] active:bg-white/[0.08] transition-colors"
      style={style}
    />
  );
}

function PlateInput({ value, color, style, testid }) {
  return (
    <AutoFitPlate value={value} color={color} style={style} testid={testid} />
  );
}

// Auto-scales text to fit the plate width. Uses a layout-pass measurement
// loop so long names shrink and short names bloom to fill the plate.
function AutoFitPlate({ value, color, style, testid }) {
  const boxRef = useRef(null);
  const textRef = useRef(null);
  const displayValue = String(value || "").slice(0, 18).toUpperCase();

  useEffect(() => {
    const box = boxRef.current;
    const el = textRef.current;
    if (!box || !el) return undefined;
    const fit = () => {
      const boxW = box.clientWidth;
      const boxH = box.clientHeight;
      if (!boxW || !boxH) return;
      // Start large, shrink until it fits with a 6% side padding.
      const maxByHeight = Math.floor(boxH * 0.78);
      const minSize = 8;
      let size = maxByHeight;
      el.style.fontSize = `${size}px`;
      while (
        (el.scrollWidth > boxW * 0.94 || el.scrollHeight > boxH * 0.94) &&
        size > minSize
      ) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(box);
    return () => ro.disconnect();
  }, [displayValue]);

  return (
    <div
      ref={boxRef}
      data-testid={testid}
      className="absolute z-20 flex items-center justify-center overflow-hidden"
      style={{ ...style, background: "rgba(3,8,12,0.97)" }}
    >
      <span
        ref={textRef}
        className="whitespace-nowrap px-1"
        style={{
          color: "#f2f5f7",
          fontWeight: 900,
          letterSpacing: "0.4px",
          fontFamily: "Arial, Helvetica, sans-serif",
          lineHeight: 1,
          textShadow: `0 0 6px ${color}55`,
        }}
      >
        {displayValue}
      </span>
    </div>
  );
}

function ScoreDisplay({ score, color, style, testid }) {
  return (
    <div
      data-testid={testid}
      className="absolute z-20 flex items-center justify-center"
      style={{
        ...style,
        background: "rgba(3,8,12,0.99)",
        color,
        fontFamily: "'Arial Narrow', Arial, sans-serif",
        fontSize: "clamp(18px, 9cqw, 60px)",
        fontWeight: 800,
        letterSpacing: "-2px",
        lineHeight: 1,
      }}
    >
      {String(score).padStart(2, "0")}
    </div>
  );
}
